import { useState, useMemo } from 'react';
import { Trash2, Loader2, Pencil, RefreshCw, CheckCircle, AlertCircle, Clock, FolderInput, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAssets } from '@/hooks/useAssets';
import { useFolders } from '@/hooks/useFolders';
import { useNotes } from '@/hooks/useNotes';
import { useAssetAnalysis } from '@/hooks/useCopywriting';
import { FolderManager } from './FolderManager';
import { AssetUploader } from './AssetUploader';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import type { Asset, AssetAnalysisStatus } from '@/types';

// 视觉元数据常量定义
const COMPOSITION_BASE = [
  { value: 'Close_up', label: '局部特写' },
  { value: 'Object_centered', label: '单品主体' },
  { value: 'Flat_lay', label: '垂直平铺' },
  { value: 'Mirror_selfie', label: '对镜拍' },
  { value: 'POV', label: '第一人称' },
  { value: 'Environment_shot', label: '大场景' },
  { value: 'Poster_style', label: '海报风格' },
  { value: 'Digital_capture', label: '数字截图' },
  { value: 'Empty_space', label: '留白/空镜' },
];

const COMPOSITION_FUNC = [
  { value: 'Safe_margin', label: '四边留白' },
  { value: 'Top_bottom_clear', label: '上下留空' },
  { value: 'Visual_clutter', label: '视觉密集' },
];

const ALL_COMPOSITION = [...COMPOSITION_BASE, ...COMPOSITION_FUNC];

const PURE_HUES = [
  { value: 'Red', label: '红' },
  { value: 'Orange', label: '橘/橙' },
  { value: 'Yellow_Beige', label: '黄/米/奶' },
  { value: 'Green', label: '绿' },
  { value: 'Blue', label: '蓝' },
  { value: 'Purple_Pink', label: '紫/粉' },
  { value: 'Brown', label: '棕/褐/木' },
  { value: 'Black_White_Grey', label: '黑/白/灰' },
  { value: 'Multicolor', label: '五彩' },
];

const TONALITIES = [
  { value: 'Vibrant', label: '高饱和/鲜艳' },
  { value: 'Muted', label: '灰调/莫兰迪' },
  { value: 'High_key', label: '高明度/通透' },
  { value: 'Low_key', label: '低明度/暗调' },
];

// 标签展示与删除组件
interface TagBadgeProps {
  label: string;
  onDelete: () => void;
  variant?: 'primary' | 'success' | 'warning' | 'info';
}

function TagBadge({ label, onDelete, variant = 'info' }: TagBadgeProps) {
  const variantStyles = {
    primary: "bg-blue-50 text-blue-600 border-blue-100 hover:bg-blue-100",
    success: "bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100",
    warning: "bg-amber-50 text-amber-600 border-amber-100 hover:bg-amber-100",
    info: "bg-slate-50 text-slate-600 border-slate-100 hover:bg-slate-100",
  };

  return (
    <Badge 
      variant="outline" 
      className={cn(
        "px-2 py-0.5 flex items-center gap-1 font-medium transition-colors border shadow-none",
        variantStyles[variant]
      )}
    >
      {label}
      <button 
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="hover:bg-black/5 rounded-full p-0.5 transition-colors"
      >
        <X className="w-3 h-3" />
      </button>
    </Badge>
  );
}

interface AssetGridProps {
  projectId: string;
  selectedFolderId: string | null;
  onSelectFolder: (folderId: string | null) => void;
}

// 分析状态徽章配置
const statusConfig: Record<AssetAnalysisStatus, { label: string; icon: typeof CheckCircle; className: string }> = {
  pending: { label: '待分析', icon: Clock, className: 'bg-gray-500/80' },
  analyzing: { label: '分析中', icon: Loader2, className: 'bg-blue-500/80' },
  completed: { label: '已分析', icon: CheckCircle, className: 'bg-green-500/80' },
  failed: { label: '分析失败', icon: AlertCircle, className: 'bg-red-500/80' },
};

export function AssetGrid({ projectId, selectedFolderId, onSelectFolder }: AssetGridProps) {
  const { 
    assets, 
    isLoading: isAssetsLoading, 
    deleteAsset, 
    deleteAssets, 
    updateAsset, 
    updateAnalysisStatus, 
    updateAssetsAnalysis, 
    updateMultipleAnalysisStatus,
    moveAssetsToFolder
  } = useAssets(projectId);

  // 获取文件夹列表
  const { folders } = useFolders(projectId);
  const { notes, isLoading: isNotesLoading } = useNotes(projectId);
  const { analyzeAssets, isAnalyzing: isGlobalAnalyzing } = useAssetAnalysis();
  
  const isLoading = isAssetsLoading || isNotesLoading;

  // 过滤后的素材
  const filteredAssets = useMemo(() => {
    if (selectedFolderId === null) return assets;
    return assets.filter(a => a.folder_id === selectedFolderId);
  }, [assets, selectedFolderId]);

  // 提取所有笔记中使用的素材 ID，作为 usedInNoteIds 的补充/兜底
  const assetIdsInNotes = useMemo(() => {
    const ids = new Set<string>();
    notes.forEach(note => {
      note.images?.forEach(img => {
        if (img.asset_ids) img.asset_ids.forEach(id => ids.add(id));
      });
    });
    return ids;
  }, [notes]);
  
  // 选择状态管理
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // 编辑/查看弹窗状态
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editScene, setEditScene] = useState('');
  const [editMood, setEditMood] = useState('');
  const [editComposition, setEditComposition] = useState<string[]>([]);
  const [editPureHue, setEditPureHue] = useState('');
  const [editTonality, setEditTonality] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isReanalyzing, setIsReanalyzing] = useState<string | null>(null);

  const handleDelete = async (assetId: string) => {
    if (confirm('确定要删除这张素材吗？')) {
      await deleteAsset(assetId);
      toast.success('素材已删除');
      // 如果被选中，移除选中状态
      if (selectedIds.has(assetId)) {
        const newSelected = new Set(selectedIds);
        newSelected.delete(assetId);
        setSelectedIds(newSelected);
      }
    }
  };

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedIds.size === 0) return;
    if (confirm(`确定要删除选中的 ${selectedIds.size} 张素材吗？`)) {
      try {
        await deleteAssets(Array.from(selectedIds));
        toast.success(`已删除 ${selectedIds.size} 张素材`);
        setSelectedIds(new Set());
      } catch (error) {
        toast.error('删除失败');
      }
    }
  };

  // 批量移动到文件夹
  const handleBatchMove = async (folderId: string | null) => {
    if (selectedIds.size === 0) return;
    try {
      await moveAssetsToFolder(Array.from(selectedIds), folderId);
      const folderName = folderId ? folders.find(f => f.id === folderId)?.name : '未分类';
      toast.success(`已将 ${selectedIds.size} 张素材移动到 "${folderName}"`);
      setSelectedIds(new Set());
    } catch (error) {
      toast.error('移动失败');
    }
  };

  // 批量分析
  const handleBatchAnalyze = async () => {
    if (selectedIds.size === 0) return;
    
    const selectedAssets = assets.filter(a => selectedIds.has(a.id));
    const assetIds = selectedAssets.map(a => a.id);
    
    try {
      await updateMultipleAnalysisStatus(assetIds, 'analyzing');
      
      const result = await analyzeAssets(
        selectedAssets.map(a => ({ id: a.id, url: a.url, filename: a.filename, title: a.title }))
      );
      
      if (result?.analyses) {
        const updatePayload = result.analyses.map(item => {
          const original = selectedAssets.find(a => a.id === item.id);
          return {
            id: item.id,
            description: item.description,
            tags: item.tags,
            title: item.suggested_title || original?.title,
            scene: item.scene,
            mood: item.mood,
            composition: item.composition,
            pure_hue: item.pure_hue,
            tonality: item.tonality
          };
        });
        
        await updateAssetsAnalysis(updatePayload);
        toast.success(`已完成 ${selectedIds.size} 张素材的分析`);
        setSelectedIds(new Set());
      }
    } catch (error) {
      await updateMultipleAnalysisStatus(assetIds, 'failed');
      toast.error('分析失败');
    }
  };

  // 切换单个选择
  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // 全选/取消全选
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredAssets.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredAssets.map(a => a.id)));
    }
  };

  // 打开编辑/查看弹窗
  const handleEdit = (asset: Asset) => {
    setEditingAsset(asset);
    setEditTitle(asset.title || asset.filename.replace(/\.[^.]+$/, ''));
    setEditDescription(asset.description || '');
    setEditScene(asset.scene || '');
    setEditMood(asset.mood || '');
    setEditComposition(asset.composition || []);
    setEditPureHue(asset.pure_hue || '');
    setEditTonality(asset.tonality || '');
    setEditTags(asset.tags || []);
  };

  // 保存编辑内容
  const handleSave = async () => {
    if (!editingAsset) return;
    
    setIsSaving(true);
    try {
      await updateAsset(editingAsset.id, { 
        title: editTitle,
        description: editDescription,
        scene: editScene,
        mood: editMood,
        composition: editComposition,
        pure_hue: editPureHue,
        tonality: editTonality,
        tags: editTags
      });
      toast.success('已更新');
      setEditingAsset(null);
    } catch {
      toast.error('保存失败');
    } finally {
      setIsSaving(false);
    }
  };

  // 重新分析单个素材
  const handleReanalyze = async (asset: Asset) => {
    setIsReanalyzing(asset.id);
    try {
      await updateAnalysisStatus(asset.id, 'analyzing');
      
      const result = await analyzeAssets([
        { id: asset.id, url: asset.url, filename: asset.filename, title: asset.title }
      ]);
      
      if (result?.analyses?.[0]) {
        const item = result.analyses[0];
        // 重新分析时，优先使用 AI 建议的标题
        const newTitle = item.suggested_title || asset.title;
        
        await updateAsset(asset.id, {
          description: item.description,
          tags: item.tags,
          scene: item.scene,
          mood: item.mood,
          composition: item.composition,
          pure_hue: item.pure_hue,
          tonality: item.tonality,
          // 优先使用 AI 建议的标题
          ...(newTitle ? { title: newTitle } : {}),
          analysis_status: 'completed',
        });
        toast.success('重新分析完成');
      }
    } catch {
      await updateAnalysisStatus(asset.id, 'failed');
      toast.error('分析失败');
    } finally {
      setIsReanalyzing(null);
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center border rounded-lg bg-muted/10 min-h-[400px]">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>加载素材中...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* 顶部：文件夹管理 (已改为水平布局) */}
      <FolderManager 
        projectId={projectId} 
        selectedFolderId={selectedFolderId}
        onSelectFolder={onSelectFolder}
      />

      {/* 下方：素材列表 */}
      <div className="flex-1 space-y-4">
        {/* 批量操作栏 (始终显示，因为包含上传按钮) */}
        <div className="flex items-center justify-between bg-muted/30 p-2 rounded-lg border border-border/50">
          <div className="flex items-center gap-3">
            {filteredAssets.length > 0 && (
              <div className="flex items-center gap-2 px-2">
                <Checkbox 
                  id="select-all"
                  checked={selectedIds.size === filteredAssets.length}
                  onCheckedChange={toggleSelectAll}
                />
                <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                  全选 ({selectedIds.size}/{filteredAssets.length})
                </label>
              </div>
            )}
            
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2 border-l pl-3 ml-1 animate-in fade-in slide-in-from-left-2 duration-200">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 gap-1.5">
                      <FolderInput className="w-3.5 h-3.5" />
                      移动到
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuLabel>选择分组</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleBatchMove(null)}>
                      未分类
                    </DropdownMenuItem>
                    {folders.map(folder => (
                      <DropdownMenuItem key={folder.id} onClick={() => handleBatchMove(folder.id)}>
                        {folder.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button 
                  variant="outline" 
                  size="sm" 
                  className="h-8 gap-1.5"
                  onClick={handleBatchAnalyze}
                  disabled={isGlobalAnalyzing}
                >
                  {isGlobalAnalyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  分析
                </Button>
                <Button 
                  variant="destructive" 
                  size="sm" 
                  className="h-8 gap-1.5"
                  onClick={handleBatchDelete}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  删除
                </Button>
              </div>
            )}
          </div>

          <AssetUploader projectId={projectId} folderId={selectedFolderId} />
        </div>

        {assets.length === 0 ? (
          <div className="h-full flex items-center justify-center border rounded-lg bg-muted/10 min-h-[400px]">
            <p className="text-muted-foreground">暂无素材，请先上传</p>
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className="h-[400px] flex flex-col items-center justify-center border border-dashed rounded-lg bg-muted/5">
            <p className="text-muted-foreground">此分组下暂无素材</p>
            <Button variant="link" onClick={() => onSelectFolder(null)}>查看全部素材</Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredAssets.map((asset) => {
                  const status = asset.analysis_status || 'pending';
                  const config = statusConfig[status];
                  const StatusIcon = config.icon;
                  const isAnalyzing = isReanalyzing === asset.id || status === 'analyzing';
                  const isSelected = selectedIds.has(asset.id);
                  
                  // 显示标题：优先使用 title，否则使用处理过的 filename
                  const displayTitle = asset.title || asset.filename.replace(/\.[^.]+$/, '');

                  return (
                    <Card 
                      key={asset.id} 
                      className={cn(
                        "group relative overflow-hidden bg-card flex flex-col border shadow-sm hover:shadow-md transition-all p-0 gap-0",
                        isSelected && "ring-2 ring-primary border-primary"
                      )}
                    >
                      {/* 图片区域 - 保持比例 */}
                      <div className="relative bg-muted/20 aspect-[3/4] overflow-hidden flex items-start justify-center">
                        <img 
                          src={asset.thumbnail_url || asset.url} 
                          alt={displayTitle} 
                          className="w-full h-full object-cover block"
                          loading="lazy"
                        />
                        
                        {/* 圆形选择勾选框 (右上角) */}
                        <div 
                          className={cn(
                            "absolute top-2 right-2 z-20 transition-opacity cursor-pointer",
                            isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSelect(asset.id);
                          }}
                        >
                          <div
                            className={cn(
                              'w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors bg-white/80 backdrop-blur',
                              isSelected
                                ? 'border-primary bg-primary text-white'
                                : 'border-gray-300 hover:border-primary'
                            )}
                          >
                            {isSelected && <Check className="w-3.5 h-3.5" />}
                          </div>
                        </div>

                        {/* 分析状态徽章 - 移至左上角 */}
                        {status !== 'completed' && (
                          <div className={cn(
                            "absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] flex items-center gap-1 shadow-sm backdrop-blur-sm z-10",
                            config.className
                          )}>
                            <StatusIcon className={cn("w-3 h-3", isAnalyzing && "animate-spin")} />
                            <span>{config.label}</span>
                          </div>
                        )}

                        {/* 已使用标记 - 移至左上角 (如果状态徽章也存在，则顺延) */}
                        {((asset.usedInNoteIds?.length ?? 0) > 0 || assetIdsInNotes.has(asset.id)) && (
                          <div className={cn(
                            "absolute px-2 py-0.5 rounded-full bg-blue-500/80 text-white text-[10px] flex items-center gap-1 shadow-sm backdrop-blur-sm z-10",
                            status !== 'completed' ? "top-8 left-2" : "top-2 left-2"
                          )}>
                            <CheckCircle className="w-3 h-3" />
                            <span>已使用</span>
                          </div>
                        )}
                        
                        {/* 悬浮操作层 */}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 z-10">
                          <Button
                            variant="secondary"
                            size="icon"
                            className="h-9 w-9 rounded-full shadow-sm hover:scale-105 transition-transform"
                            onClick={() => handleEdit(asset)}
                            title="编辑详情"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="secondary"
                                size="icon"
                                className="h-9 w-9 rounded-full shadow-sm hover:scale-105 transition-transform"
                                title="移动到分组"
                              >
                                <FolderInput className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem onClick={() => moveAssetsToFolder([asset.id], null)}>
                                未分类
                              </DropdownMenuItem>
                              {folders.map(folder => (
                                <DropdownMenuItem key={folder.id} onClick={() => moveAssetsToFolder([asset.id], folder.id)}>
                                  {folder.name}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button 
                            variant="secondary"
                            size="icon"
                            className="h-9 w-9 rounded-full shadow-sm hover:scale-105 transition-transform"
                            onClick={() => handleReanalyze(asset)}
                            disabled={isAnalyzing}
                            title="重新分析"
                          >
                            <RefreshCw className={cn("w-4 h-4", isAnalyzing && "animate-spin")} /> 
                          </Button>
                          <Button
                            variant="destructive"
                            size="icon"
                            className="h-9 w-9 rounded-full shadow-sm hover:scale-105 transition-transform"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(asset.id);
                            }}
                            title="删除素材"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* 信息区域 - 简化，仅展示标题 */}
                      <div 
                        className="px-3 py-3 flex-shrink-0 flex items-center cursor-pointer hover:bg-muted/5 transition-colors border-t"
                        onClick={() => handleEdit(asset)}
                      >
                        <h4 className="font-medium text-sm truncate w-full" title={displayTitle}>
                          {displayTitle}
                        </h4>
                      </div>
                    </Card>
                  );
                })}
          </div>
        )}
      </div>

      {/* 详情与管理弹窗 */}
      <Dialog open={!!editingAsset} onOpenChange={(open) => !open && setEditingAsset(null)}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0">
          <div className="flex flex-col md:flex-row h-full max-h-[90vh]">
            {/* 左侧：大图预览 */}
            <div className="w-full md:w-1/2 bg-black/5 flex items-center justify-center p-4 min-h-[300px] md:min-h-full overflow-auto">
              {editingAsset && (
                <img 
                  src={editingAsset.url} 
                  alt={editingAsset.filename}
                  className="max-w-full max-h-[80vh] object-contain shadow-sm rounded-md"
                />
              )}
            </div>

            {/* 右侧：展示与管理 */}
            <div className="w-full md:w-1/2 flex flex-col border-l">
              <DialogHeader className="p-6 pb-2">
                <DialogTitle>素材详情</DialogTitle>
              </DialogHeader>
              
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium">标题名称</label>
                  <Input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="素材名称..."
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">图片描述</label>
                  <Textarea
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="输入图片描述，将用于 AI 配图规划..."
                    rows={4}
                    className="resize-none"
                  />
                  <p className="text-xs text-muted-foreground">
                    详细的描述有助于 AI 更精准地将此图片匹配到合适的文案段落中。
                  </p>
                </div>

                {/* 简化后的三大维度标签展示 */}
                <div className="space-y-6 pt-2">
                  {/* 维度 1：基础属性 (场景 + 构图) */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <span className="w-1 h-4 bg-blue-500 rounded-full"></span>
                      基础属性
                    </label>
                    <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-slate-50/50 min-h-[46px]">
                      {editScene && (
                        <TagBadge 
                          label={editScene} 
                          variant="primary" 
                          onDelete={() => setEditScene('')} 
                        />
                      )}
                      {editComposition.map(val => (
                        <TagBadge 
                          key={val}
                          label={ALL_COMPOSITION.find(c => c.value === val)?.label || val} 
                          variant="primary" 
                          onDelete={() => setEditComposition(prev => prev.filter(v => v !== val))} 
                        />
                      ))}
                      {!editScene && editComposition.length === 0 && (
                        <span className="text-xs text-muted-foreground italic">暂无属性</span>
                      )}
                    </div>
                  </div>

                  {/* 维度 2：视觉风格 (氛围 + 色彩) */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <span className="w-1 h-4 bg-emerald-500 rounded-full"></span>
                      视觉风格
                    </label>
                    <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-slate-50/50 min-h-[46px]">
                      {editMood && (
                        <TagBadge 
                          label={editMood} 
                          variant="success" 
                          onDelete={() => setEditMood('')} 
                        />
                      )}
                      {editPureHue && (
                        <TagBadge 
                          label={`${PURE_HUES.find(h => h.value === editPureHue)?.label || editPureHue}色调`} 
                          variant="success" 
                          onDelete={() => setEditPureHue('')} 
                        />
                      )}
                      {editTonality && (
                        <TagBadge 
                          label={TONALITIES.find(t => t.value === editTonality)?.label || editTonality} 
                          variant="success" 
                          onDelete={() => setEditTonality('')} 
                        />
                      )}
                      {!editMood && !editPureHue && !editTonality && (
                        <span className="text-xs text-muted-foreground italic">暂无风格</span>
                      )}
                    </div>
                  </div>

                  {/* 维度 3：内容标签 */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <span className="w-1 h-4 bg-amber-500 rounded-full"></span>
                      内容标签
                    </label>
                    <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-slate-50/50 min-h-[46px]">
                      {editTags.length > 0 ? (
                        editTags.map((tag, idx) => (
                          <TagBadge 
                            key={idx}
                            label={tag} 
                            variant="warning" 
                            onDelete={() => setEditTags(prev => prev.filter((_, i) => i !== idx))} 
                          />
                        ))
                      ) : (
                        <span className="text-xs text-muted-foreground italic">暂无内容标签</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <DialogFooter className="p-6 border-t bg-muted/10">
                <Button variant="outline" onClick={() => setEditingAsset(null)}>
                  取消
                </Button>
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  保存修改
                </Button>
              </DialogFooter>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
